import { useId, useRef } from 'react';
import { ChevronDown, CircleHelp } from 'lucide-react';
import '../analytics.css';

export interface AnalyticsBannerItem {
  id: string;
  label: string;
  value: string;
  description: string;
  status?: 'loading' | 'ready' | 'stale' | 'unavailable';
}

interface AnalyticsBannerProps {
  items: readonly AnalyticsBannerItem[];
  /** Milliseconds since the Unix epoch, for the last successful rollup. */
  updatedAt?: number;
  /** Milliseconds since the Unix epoch, when the usage counters began. */
  trackingSince?: number;
  error?: string;
}

const statusLabels = { loading: 'Loading', ready: '', stale: 'Delayed', unavailable: 'Unavailable' };

function timestamp(value?: number) {
  if (!value || !Number.isFinite(value) || value <= 0) return undefined;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : undefined;
}

export function AnalyticsBanner({ items, updatedAt, trackingSince, error }: AnalyticsBannerProps) {
  const id = useId(), details = useRef<HTMLDetailsElement>(null);
  const updated = timestamp(updatedAt), since = timestamp(trackingSince);
  return <section className="analytics-banner" aria-label="Soonscreener platform statistics" onKeyDown={event => {
    if (event.key === 'Escape' && details.current?.open) {
      details.current.open = false;
      details.current.querySelector('summary')?.focus();
    }
  }}>
    <div className="analytics-banner-inner">
      <div className="analytics-metrics-window">
        <dl className="analytics-metrics" tabIndex={0} aria-label="Platform statistics. Scroll horizontally for more numbers.">
          {items.map(item => {
            const status = item.status ?? 'ready', suffix = statusLabels[status];
            return <div className={`analytics-metric is-${status}`} key={item.id} title={`${item.description}${suffix ? ` ${suffix}.` : ''}`}>
              <dt>{item.label}</dt>
              <dd><span>{item.value}</span>{status === 'stale' && <span className="analytics-stale-mark" aria-hidden="true">·</span>}{suffix && <span className="analytics-sr-only"> ({suffix})</span>}</dd>
            </div>;
          })}
        </dl>
      </div>
      <details className="analytics-info" ref={details}>
        <summary aria-label="Stats details" aria-controls={`${id}-definitions`} title="Statistics definitions and coverage"><CircleHelp size={15} aria-hidden="true" /><span>Stats details</span><ChevronDown className="analytics-info-chevron" size={12} aria-hidden="true" /></summary>
        <div className="analytics-definitions" id={`${id}-definitions`}>
          <div className="analytics-definitions-heading"><strong>Behind the numbers</strong><span>Definitions &amp; coverage</span></div>
          {(updated || since) && <div className="analytics-timestamps">{updated && <p>Last rollup <time dateTime={new Date(updatedAt!).toISOString()}>{updated}</time></p>}{since && <p>Usage tracking since <time dateTime={new Date(trackingSince!).toISOString()}>{since}</time></p>}</div>}
          {error && <p className="analytics-data-message">{error}</p>}
          <dl className="analytics-definition-list">{items.map(item => <div key={item.id}>
            <dt>{item.label}<span>{item.value}{item.status && item.status !== 'ready' ? ` · ${statusLabels[item.status]}` : ''}</span></dt>
            <dd>{item.description}</dd>
          </div>)}</dl>
          <p className="analytics-definitions-note">Unknown values appear as —. Browser visits and accounts do not establish a count of individual people.</p>
        </div>
      </details>
    </div>
  </section>;
}
